'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useRef, useState, type ReactNode } from 'react'

import { Check } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  SectionBadge,
  SectionHeader,
  SectionSubtitle,
  SectionTitle,
} from '@/components/marketing/section-header'
import { EvidenceGradeBadge } from '@/components/shared/evidence-grade-badge'
import { Tag } from '@/components/shared/tag'
import { getDefaultProfile } from '@/components/marketing/mini-dashboard/data/industries'
import { getSamplePlanSummary } from '@/components/marketing/mini-dashboard/data/plan-summary'
import {
  ENGINE_DATA,
  PROMPT_ROWS,
  type EngineKey,
} from '@/components/marketing/sections/feature-tabs-data'
import { formatEngineNames } from '@/lib/marketing/engines'
import { pct } from '@/lib/format'

// The sample Growth Plan the Plan tab, the Track tab and the hero mini-dashboard all read.
const PLAN_SUMMARY = getSamplePlanSummary(getDefaultProfile())

// ── Types ──────────────────────────────────────────────────────────────────────

type TabKey = 'track' | 'analyze' | 'plan'

const TARGET_KIND_LABEL = { source: 'Source', site: 'Your site' } as const

// ── Tab config ─────────────────────────────────────────────────────────────────

const TABS: {
  key: TabKey
  number: string
  heading: string
  description: string
  bullets: string[]
}[] = [
  {
    key: 'plan',
    number: '01',
    heading: 'Know which pages AI reads — and what to do on each, ranked',
    description:
      'AI engines build their answers from Reddit threads, G2 listings, review listicles and your own pages. RankSurf reads every answer to your buyer questions, finds where a competitor is cited and you are not, and turns each gap into a Suggestion in your Growth Plan: the page, who is on it, and the action. Start one as a Task and we check that it went live, then measure whether you get named.',
    bullets: [
      'Sources: every third-party page each engine cites for your buyer questions, and who it names there',
      'Suggestions ranked by how many questions and engines they reach, each marked Measured or Hypothesis',
      'Tasks checked live where possible, then re-measured every scan',
    ],
  },
  {
    key: 'analyze',
    number: '02',
    heading: "See who's beating you — and the exact pages AI trusts",
    description:
      "For every prompt where you're losing, RankSurf surfaces the full AI response — your brand flagged, competitors highlighted, and every source named. No summaries: the actual text the model produced, plus the exact third-party pages it leaned on.",
    bullets: [
      'Source analysis — the exact Reddit threads, review sites, and listicles AI trusts when it recommends your rivals',
      'Full AI response text — your brand highlighted, competitors flagged',
      'Cited but not recommended — flagged when AI quotes your page yet recommends a competitor instead',
      'Truth Accuracy Rate — instant alerts when an engine misquotes your brand',
      'Competitor share of voice — who AI recommends instead of you, and how often',
    ],
  },
  {
    key: 'track',
    number: '03',
    heading: 'Track your standing on every engine — and confirm every win',
    description: `Your prompts run across ${formatEngineNames()} multiple times a week. You see which queries you win, which you lose, whether your visibility is trending up — and whether the citations you earned off-site actually moved the needle.`,
    bullets: [
      'Per-engine status for every prompt — green or red, no ambiguity',
      'Week-over-week trends so you know if things are getting better or worse',
      "A single visibility score built from how often you actually appear — not a synthetic AI 'rank'",
    ],
  },
]

// ── Panels ─────────────────────────────────────────────────────────────────────

function StatusDot({ on }: { on: boolean }) {
  return (
    <span
      className={`inline-block size-2.5 shrink-0 rounded-full ${on ? 'bg-emerald' : 'bg-rose'}`}
    />
  )
}

/** The panel footer that says a panel's rows are a sample, not measured data (PRD-02). */
function SampleCaption({ testId, children }: { testId: string; children: ReactNode }) {
  return (
    <p
      data-testid={testId}
      className="border-t border-border bg-surface px-4 py-2 text-label text-muted-foreground"
    >
      {children}
    </p>
  )
}

function TrackPanel() {
  const [selected, setSelected] = useState<string>('best B2B CRM software')

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border bg-surface px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="size-2 rounded-full bg-emerald" />
          <span className="text-xs font-semibold text-foreground">Prompts</span>
          <span className="rounded-md bg-muted px-1.5 py-0.5 text-micro font-semibold text-muted-foreground">
            4
          </span>
        </div>
        <span className="text-micro text-muted-foreground">Last run 2h ago</span>
      </div>
      {/* Column headers — logos replace text abbreviations */}
      <div className="grid grid-cols-[1fr_32px_32px_32px_52px] items-center border-b border-border bg-surface/40 px-4 py-2">
        <span className="type-overline text-muted-foreground">Prompt</span>
        {[
          { src: '/images/ai-platforms/chatgpt-logo.svg', alt: 'ChatGPT' },
          { src: '/images/ai-platforms/perplexity-logo.svg', alt: 'Perplexity' },
          { src: '/images/ai-platforms/google-aio-logo.svg', alt: 'Google AI Overview' },
        ].map((logo) => (
          <div key={logo.alt} className="flex justify-center">
            <Image
              src={logo.src}
              alt={logo.alt}
              width={14}
              height={14}
              className="w-3.5 dark:brightness-0 dark:invert"
              style={{ height: 'auto' }}
            />
          </div>
        ))}
        <span className="type-overline text-right text-muted-foreground">Trend</span>
      </div>
      {PROMPT_ROWS.map((row) => (
        <button
          key={row.query}
          onClick={() => setSelected(row.query)}
          className={`grid w-full cursor-pointer grid-cols-[1fr_32px_32px_32px_52px] items-center border-b border-border/50 px-4 py-3 text-left transition-colors duration-150 last:border-0 ${
            selected === row.query ? 'bg-primary-100' : 'hover:bg-surface/60'
          }`}
        >
          <span className="truncate text-xs text-foreground">{row.query}</span>
          <div className="flex justify-center">
            <StatusDot on={row.chatgpt} />
          </div>
          <div className="flex justify-center">
            <StatusDot on={row.perplexity} />
          </div>
          <div className="flex justify-center">
            <StatusDot on={row.google_aio} />
          </div>
          <span
            className={`text-right text-xs font-semibold ${
              row.up === true
                ? 'text-emerald'
                : row.up === false
                  ? 'text-rose'
                  : 'text-muted-foreground'
            }`}
          >
            {`${row.up === true ? '↑' : row.up === false ? '↓' : '→'} ${row.trend}`}
          </span>
        </button>
      ))}
      <div className="flex items-center justify-between border-t border-border bg-surface px-4 py-2.5">
        <span className="text-xs text-muted-foreground">
          Visibility:{' '}
          <span className="font-semibold text-foreground">
            {pct(PLAN_SUMMARY.visibility / 100)}
          </span>
        </span>
        {PLAN_SUMMARY.visibilityDelta > 0 && (
          <span className="text-xs font-semibold text-emerald">
            ↑ +{pct(PLAN_SUMMARY.visibilityDelta / 100)} this week
          </span>
        )}
      </div>
      <SampleCaption testId="feature-track-panel-caption">
        Illustrative rows for a CRM brand, not measured data.
      </SampleCaption>
    </div>
  )
}

function AnalyzePanel() {
  const [activeEngine, setActiveEngine] = useState<EngineKey>('chatgpt')
  const data = ENGINE_DATA[activeEngine]

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="flex gap-0.5 border-b border-border bg-surface px-3 py-2">
        {(['chatgpt', 'perplexity', 'google_aio'] as EngineKey[]).map((engine) => (
          <button
            key={engine}
            onClick={() => setActiveEngine(engine)}
            className={`flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
              activeEngine === engine
                ? 'bg-card text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Image
              src={ENGINE_DATA[engine].logo}
              alt={ENGINE_DATA[engine].label}
              width={12}
              height={12}
              className="w-3 dark:brightness-0 dark:invert"
              style={{ height: 'auto' }}
            />
            {ENGINE_DATA[engine].label}
          </button>
        ))}
      </div>
      <div className="border-b border-border/50 bg-muted/30 px-4 py-2">
        <span className="type-caption text-muted-foreground">Prompt: </span>
        <span className="type-caption font-semibold text-foreground">
          &ldquo;best B2B CRM software&rdquo;
        </span>
      </div>
      <div
        key={activeEngine}
        className="animate-in fade-in-0 slide-in-from-bottom-3 duration-300 px-4 py-4"
      >
        <p className="text-xs leading-relaxed text-foreground">
          &ldquo;
          {data.parts.map((part, i) => {
            if (part.type === 'brand') {
              return (
                <mark
                  key={i}
                  className="rounded bg-emerald-bg px-0.5 font-semibold text-emerald not-italic"
                >
                  {part.text}
                </mark>
              )
            }
            if (part.type === 'competitor') {
              return (
                <mark key={i} className="rounded bg-rose-bg px-0.5 text-rose not-italic">
                  {part.text}
                </mark>
              )
            }
            return <span key={i}>{part.text}</span>
          })}
          &rdquo;
        </p>
      </div>
      <div className="flex flex-wrap gap-1.5 border-t border-border/50 px-4 py-3">
        {data.position !== null ? (
          <span className="rounded-full bg-emerald-bg px-2 py-0.5 text-micro font-semibold text-emerald">
            Ranked #{data.position} of {data.total}
          </span>
        ) : (
          <span className="rounded-full bg-rose-bg px-2 py-0.5 text-micro font-semibold text-rose">
            Not mentioned
          </span>
        )}
        <span className="rounded-full bg-muted px-2 py-0.5 text-micro text-muted-foreground">
          {data.sources.length} sources cited
        </span>
      </div>
      <div className="border-t border-border px-4 py-3">
        <p className="type-overline mb-2 text-muted-foreground">Sources cited</p>
        <div className="space-y-1">
          {data.sources.map((src) => (
            <div key={src} className="flex items-center gap-2">
              <span className="size-1.5 shrink-0 rounded-full bg-muted-foreground/30" />
              <span className="text-label text-muted-foreground">{src}</span>
            </div>
          ))}
        </div>
      </div>
      <SampleCaption testId="feature-analyze-panel-caption">
        Illustrative answers for a CRM brand, not measured data.
      </SampleCaption>
    </div>
  )
}

/**
 * The Growth Plan's open Suggestions, widest reach first. The count pill is the open total and the
 * panel shows the first few, so the cap is stated in the caption (PRD-01), which also says the rows
 * are a sample, not measured (PRD-02). Row 1 starts open.
 */
function PlanPanel() {
  const [expanded, setExpanded] = useState<string | null>(PLAN_SUMMARY.upNext[0]?.id ?? null)

  return (
    <div
      data-testid="feature-plan-panel"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="flex items-center justify-between border-b border-border bg-surface px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-foreground">Growth Plan · Suggestions</span>
          <span className="rounded-md bg-muted px-1.5 py-0.5 text-micro font-semibold text-muted-foreground">
            {PLAN_SUMMARY.suggestionCount}
          </span>
        </div>
        <span className="text-micro text-muted-foreground">Ranked by reach</span>
      </div>
      {PLAN_SUMMARY.upNext.map((suggestion, index) => {
        const isOpen = expanded === suggestion.id
        return (
          <button
            key={suggestion.id}
            type="button"
            aria-expanded={isOpen}
            onClick={() => setExpanded(isOpen ? null : suggestion.id)}
            className={`flex w-full cursor-pointer items-start gap-3 border-b border-border/50 px-4 py-3.5 text-left transition-colors duration-150 last:border-0 ${
              isOpen ? 'bg-surface/50' : 'hover:bg-surface/40'
            }`}
          >
            <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-micro font-semibold text-muted-foreground">
              {index + 1}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground">{suggestion.title}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Tag bordered>{TARGET_KIND_LABEL[suggestion.targetKind]}</Tag>
                <EvidenceGradeBadge grade={suggestion.grade} />
                {(suggestion.buyerQuestions ?? 0) > 0 && (
                  <Tag>
                    {`${suggestion.buyerQuestions} buyer ${
                      suggestion.buyerQuestions === 1 ? 'question' : 'questions'
                    }`}
                  </Tag>
                )}
              </div>
              {/* Expand / collapse the Suggestion's reason */}
              <div
                className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                  isOpen ? '[grid-template-rows:1fr]' : '[grid-template-rows:0fr]'
                }`}
              >
                <div className="overflow-hidden">
                  <p className="pt-2 text-label leading-relaxed text-muted-foreground">
                    {suggestion.reason}
                  </p>
                </div>
              </div>
            </div>
          </button>
        )
      })}
      <SampleCaption testId="feature-plan-panel-caption">
        {`Illustrative rows for a CRM brand, not measured data.${
          PLAN_SUMMARY.suggestionCount > PLAN_SUMMARY.upNext.length
            ? ` Showing ${PLAN_SUMMARY.upNext.length} of ${PLAN_SUMMARY.suggestionCount}.`
            : ''
        }`}
      </SampleCaption>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

const TAB_PANEL_IDS: Record<TabKey, string> = {
  track: 'feature-panel-track',
  analyze: 'feature-panel-analyze',
  plan: 'feature-panel-plan',
}

const TAB_BUTTON_IDS: Record<TabKey, string> = {
  track: 'feature-tab-track',
  analyze: 'feature-tab-analyze',
  plan: 'feature-tab-plan',
}

export function FeatureTabsSection() {
  const [activeTab, setActiveTab] = useState<TabKey>('plan')
  const tabRefs = useRef<Record<TabKey, HTMLButtonElement | null>>({
    plan: null,
    analyze: null,
    track: null,
  })

  const TAB_KEYS: TabKey[] = ['plan', 'analyze', 'track']

  function handleTabKeyDown(e: React.KeyboardEvent, currentKey: TabKey) {
    const currentIndex = TAB_KEYS.indexOf(currentKey)
    let nextIndex: number | null = null
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      nextIndex = (currentIndex + 1) % TAB_KEYS.length
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      nextIndex = (currentIndex - 1 + TAB_KEYS.length) % TAB_KEYS.length
    } else if (e.key === 'Home') {
      e.preventDefault()
      nextIndex = 0
    } else if (e.key === 'End') {
      e.preventDefault()
      nextIndex = TAB_KEYS.length - 1
    }
    if (nextIndex !== null) {
      const nextKey = TAB_KEYS[nextIndex]!
      setActiveTab(nextKey)
      tabRefs.current[nextKey]?.focus()
    }
  }

  return (
    <section id="features" className="marketing-section bg-background">
      <div className="wrap-wide">
        <SectionHeader align="split">
          <SectionBadge dot>Off-site GEO</SectionBadge>
          <SectionTitle>
            AI builds its answers from third-party pages — we show you which ones, and what to do on
            each
          </SectionTitle>
          <SectionSubtitle>
            ChatGPT, Perplexity, and Google AI Overview don&apos;t invent recommendations — they
            repeat the Reddit threads, G2 listings, and review listicles they already trust.
            RankSurf shows you exactly which third-party pages each engine read to answer your
            prompts, how often it reads each one, which brands it named in those same answers, and a
            Suggestion for each gap in your Growth Plan.
          </SectionSubtitle>
        </SectionHeader>

        <div className="marketing-gap-content grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-x-8">
          {/* Vertical tab list — ARIA tablist pattern with arrow-key navigation */}
          <div
            role="tablist"
            aria-label="Product features"
            aria-orientation="vertical"
            className="space-y-1"
          >
            {TABS.map((t) => {
              const isActive = activeTab === t.key
              return (
                <button
                  key={t.key}
                  id={TAB_BUTTON_IDS[t.key]}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={TAB_PANEL_IDS[t.key]}
                  tabIndex={isActive ? 0 : -1}
                  ref={(el) => {
                    tabRefs.current[t.key] = el
                  }}
                  onClick={() => setActiveTab(t.key)}
                  onKeyDown={(e) => handleTabKeyDown(e, t.key)}
                  className={`relative w-full cursor-pointer overflow-hidden rounded-lg border text-left transition-all duration-200 ${
                    isActive
                      ? 'border-border bg-card shadow-[inset_2px_0_0_0] shadow-foreground/20'
                      : 'border-border/40 hover:border-border/70'
                  }`}
                >
                  <div className="px-5 py-4">
                    <div className="flex items-baseline gap-3">
                      <span
                        className={`w-8 shrink-0 font-mono text-sm font-semibold transition-colors duration-200 ${
                          isActive ? 'text-foreground' : 'text-muted-foreground'
                        }`}
                      >
                        {t.number}.
                      </span>
                      <span
                        className={`text-sm font-semibold leading-snug transition-colors duration-200 ${
                          isActive ? 'text-foreground' : 'text-muted-foreground'
                        }`}
                      >
                        {t.heading}
                      </span>
                    </div>

                    {/* Collapsible description */}
                    <div
                      className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                        isActive ? '[grid-template-rows:1fr]' : '[grid-template-rows:0fr]'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="pl-11 pt-2 pb-1 text-sm text-muted-foreground">
                          {t.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>

          {/* Panel + bullet list */}
          <div
            key={activeTab}
            id={TAB_PANEL_IDS[activeTab]}
            role="tabpanel"
            aria-labelledby={TAB_BUTTON_IDS[activeTab]}
            tabIndex={0}
            className="animate-in fade-in-0 slide-in-from-bottom-3 duration-300 outline-none"
          >
            {activeTab === 'track' && <TrackPanel />}
            {activeTab === 'analyze' && <AnalyzePanel />}
            {activeTab === 'plan' && <PlanPanel />}

            {/* Bullets below the panel */}
            <ul className="mt-5 space-y-2">
              {TABS.find((t) => t.key === activeTab)!.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm">
                  <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <Check className="size-2.5" strokeWidth={3} aria-hidden="true" />
                  </span>
                  <span className="text-foreground">{b}</span>
                </li>
              ))}
            </ul>

            {/* CTA — canonical "Run a free scan" */}
            <div className="mt-8 text-center">
              <Button
                render={<Link href="/tools/ai-visibility-scan" />}
                nativeButton={false}
                variant="default"
                size="default"
              >
                Run a free scan
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
